import {
    Button, Form, Spin, Tooltip, Typography,
} from 'antd';
import React, {
    FC,
} from 'react';

import {StaticFormItem} from 'components/@common/inputs/static-form-item/static-form-item';
import {ActionBar} from 'components/@common/widgets/action-bar';
import {CustomCard} from 'components/@common/widgets/custom-card';
import {CustomTable, customTableUtils} from 'components/@common/widgets/custom-table';
import {MonitorServicesModal} from 'components/ais/monitor-services-card/monitor-services-modal';
import {CustomSelect} from 'components/form/inputs/custom-select';
import {ModalOpenerComponent} from 'components/modal-opener-component';
import {formatDate} from 'shared/utils/date-time';

import {useAisAnswerFormationNdPage} from '../hooks';
import {
    ECommentRequestStatuses,
    COMMENTS_REQUEST_MONITOR_SERVICE_LOOKUP_CODE,
    formRowCommentsTableHeader,
} from '../model/constants';
import s from './ais-answer-formation-nd-page.module.less';
import {AisCommentRequestRowModalOpener} from './modals';

const {Paragraph} = Typography;

interface IAisAnswerFormationNdPageProps {
  entityName: string;
}

export const AisAnswerFormationNdPage: FC<IAisAnswerFormationNdPageProps> = ({
    entityName,
}: IAisAnswerFormationNdPageProps) => {
    const {
        isModalOpen,
        isMonitorServiceModalOpen,
        currentRequestId,
        form,
        aisCommentsRequestTable,
        commentsRequestInfo,
        isLoadingRequestNumber,
        isLoadingTable,
        taxCodes,
        setIsMonitorServiceModalOpen,
        setCurrentRequestId,
        fetchTableData,
        setIsModalOpen,
    } = useAisAnswerFormationNdPage();

    return (
        <CustomCard
            title="Запросы на предоставление комментариев к результатам ФА НДПИ, НДД, Акциза на нефтяное сырье"
            accordionTransitionType="1"
            accordionContent={(
                <Form
                    className={s['answer-formation-form']}
                    layout="vertical"
                    form={form}
                >
                    <div
                        className={s['answer-formation-form__row']}
                    >
                        <Form.Item
                            className={s['answer-formation-form__item']}
                            name="id"
                            label="Номер запроса"
                        >
                            <CustomSelect
                                entries={aisCommentsRequestTable?.map(e => ({
                                    label: e.numberRequest,
                                    value: e.id,
                                }))}
                                onChange={v => setCurrentRequestId(v)}
                                settings={{
                                    showSearch: true,
                                    placeholder: 'Выберите номер запроса',
                                    isLoading: isLoadingRequestNumber,
                                }}
                                fixedDropdown={false}
                            />
                        </Form.Item>

                        <StaticFormItem
                            classNames={{
                                wrapperClassName: s['form-item'],
                                valueClassName: s['text-gray'],
                            }}
                            label="Статус"
                            value={
                                commentsRequestInfo?.status
                                    ? ECommentRequestStatuses.COMPLETED
                                    : ECommentRequestStatuses.NEW
                            }
                        />

                        {commentsRequestInfo?.taxCode && (
                            <StaticFormItem
                                classNames={{
                                    wrapperClassName: s['form-item'],
                                }}
                                label="Налог"
                                customValue={(
                                    <Typography.Text className={s['text-gray']}>
                                        {taxCodes[String(commentsRequestInfo?.taxCode)] as string
                                    || commentsRequestInfo?.taxCode}
                                    </Typography.Text>
                                )}
                            />
                        )}

                        {commentsRequestInfo?.createdRequestDate && (
                            <StaticFormItem
                                classNames={{
                                    wrapperClassName: s['form-item'],
                                    valueClassName: s['text-gray'],
                                }}
                                label="Дата получения запроса"
                                value={formatDate(commentsRequestInfo?.createdRequestDate)}
                            />
                        )}

                        {commentsRequestInfo?.plannedResponseDate && (
                            <StaticFormItem
                                classNames={{
                                    wrapperClassName: s['form-item'],
                                    valueClassName: s['text-gray'],
                                }}
                                label="Плановая дата отправки ответа"
                                value={formatDate(commentsRequestInfo?.plannedResponseDate)}
                            />
                        )}

                        {commentsRequestInfo?.createdResponseDate && (
                            <StaticFormItem
                                classNames={{
                                    wrapperClassName: s['form-item'],
                                    valueClassName: s['text-gray'],
                                }}
                                label="Фактическая дата отправки ответа"
                                value={formatDate(commentsRequestInfo?.createdResponseDate)}
                            />
                        )}

                    </div>

                    {commentsRequestInfo?.inspectorComment
                    && (
                        <Form.Item
                            label="Комментарий ФНС"
                            style={{width: '100%', marginBottom: 0}}
                        >
                            <div onClick={() => setIsModalOpen(true)}>
                                <Tooltip
                                    title="Просмотреть комментарий целиком"
                                    mouseEnterDelay={0.8}
                                    placement="bottomLeft"
                                >
                                    <Spin
                                        spinning={isLoadingTable}
                                        tip="Загрузка комментария..."
                                    >
                                        <Paragraph
                                            ellipsis={{rows: 3}}
                                            className={s['answer-formation-form__text']}
                                        >
                                            {commentsRequestInfo?.inspectorComment}
                                        </Paragraph>
                                    </Spin>
                                </Tooltip>
                            </div>
                        </Form.Item>
                    )}
                </Form>
            )}
            isAccordionContentVisibleByDefault
            className={s['answer-formation']}
            bodyStyle={{paddingTop: 0}}
        >
            <ActionBar
                side="left"
                className={s['answer-formation__buttons-bar']}
                transparent
            >
                {!commentsRequestInfo?.status && (
                    <Button
                        type="primary"
                        disabled={isLoadingTable || isLoadingRequestNumber}
                        onClick={() => setIsMonitorServiceModalOpen(true)}
                    >
                        Отправить ответ
                    </Button>
                )}

                <MonitorServicesModal
                    entityName={entityName}
                    visible={isMonitorServiceModalOpen}
                    onFormClose={() => setIsMonitorServiceModalOpen(false)}
                    initialValues={{
                        externalServiceLookupCode: COMMENTS_REQUEST_MONITOR_SERVICE_LOOKUP_CODE,
                        formInitialValues: {
                            requestId: aisCommentsRequestTable?.find(
                                e => e.id === Number(currentRequestId),
                            )?.numberRequest,
                        },
                    }}
                    disabled
                />
            </ActionBar>

            <Spin
                spinning={isLoadingTable}
                tip="Загрузка..."
            >
                <CustomTable
                    rowKey="id"
                    className={s['answer-formation__table']}
                    columns={[{
                        title: '№ показателя',
                        dataIndex: 'indicatorNumber',
                        key: 'indicatorNumber',
                        align: 'center',
                        render: (_, row) => (
                            <AisCommentRequestRowModalOpener
                                row={row}
                                fetchTableData={fetchTableData}
                                status={
                                    commentsRequestInfo?.status
                                        ? ECommentRequestStatuses.COMPLETED
                                        : ECommentRequestStatuses.NEW
                                }
                            />
                        ),
                    },
                    ...formRowCommentsTableHeader(
                        commentsRequestInfo?.comparedYear,
                        commentsRequestInfo?.reportingYear,
                        commentsRequestInfo?.taxPeriod,
                    )]}
                    dataSource={currentRequestId ? commentsRequestInfo?.detailsComments : []}
                    onColumn={col => ({
                        ...customTableUtils.setHeaderStyles({
                            whiteSpace: 'pre-wrap',
                            textAlign: 'center',
                        }),
                        ...col,
                    })}
                    bordered
                />
            </Spin>

            <ModalOpenerComponent
                modalProps={{
                    title: 'Комментарий ФНС',
                    width: '980px',
                    centered: true,
                }}
                controlLabels={{
                    cancel: 'Закрыть',
                }}
                isOpen={isModalOpen}
                handleCancel={() => setIsModalOpen(false)}
            >
                {commentsRequestInfo?.inspectorComment ?? ''}
            </ModalOpenerComponent>
        </CustomCard>
    );
};
